import { useContext } from 'react';

import { getGlobalObject } from '../getGlobalObject.js';
import { RumComponentContext } from './rumComponentContext.js';

/**
 * A hook that allows tracking of actions in Real User Monitoring (RUM).
 * It automatically adds the component chain/breadcrumbs from <RumComponentContextProvider>.
 * You can add a "purpose" to the custom attributes to group the actions.
 *
 * @function
 * @param purpose - Explains the use case for the action. This allows for splitting performance and user-tracking actions, for example.
 *
 * @returns - A function that can be used to track a RUM action. If the Datadog RUM SDK is not initialized, it returns a no-op function.
 *
 * @throws - If the Datadog RUM SDK is not initialized, a warning is logged to the console.
 */
export const useRumAction = (purpose = 'unknown') => {
  const componentContext = useContext(RumComponentContext);
  // @ts-ignore DD_RUM is defined in the SDK
  const RumGlobal = getGlobalObject<Window>().DD_RUM;

  if (!RumGlobal) {
    console.warn(
      '@datadog/rum-react-integration: Datadog RUM SDK is not initialized.',
    );
    return () => {};
  }

  return (name: string, customAttributes?: object) => {
    RumGlobal.addAction(name, {
      purpose,
      ...componentContext.customAttributes,
      ...customAttributes,
      react: {
        breadcrumbs: componentContext.componentBreadCrumbs,
        ...(customAttributes as any)?.react,
      },
    });
  };
};
